import {
  cmApi,
  type AggregateMemberGroupRow,
  type AggregateMemberListItem,
  type AggregateMembersListData,
} from '@/api/cmApi'
import { authHeaders } from '@/auth/session'

/** Lab Aggregate Members page size (`maxRow` / uib-pagination 50). */
export const AGGREGATE_MEMBERS_PAGE_SIZE = 50

export type AggregateMembersListResult = {
  rows: AggregateMemberGroupRow[]
  total: number
  pageSize: number
}

function isListData(v: unknown): v is AggregateMembersListData {
  if (!v || typeof v !== 'object') return false
  const o = v as Record<string, unknown>
  return Array.isArray(o.outputArray) && typeof o.outputCount === 'number'
}

/** Peel Standard `{ status, message, data }` for Aggregate Members list. */
export function unwrapAggregateMembersList(res: unknown): AggregateMembersListResult {
  let cur: unknown = res
  for (let i = 0; i < 4; i++) {
    if (isListData(cur)) {
      return {
        rows: cur.outputArray,
        total: cur.outputCount,
        pageSize: Number(cur.maxRow) > 0 ? Number(cur.maxRow) : AGGREGATE_MEMBERS_PAGE_SIZE,
      }
    }
    if (!cur || typeof cur !== 'object' || !('data' in cur)) break
    cur = (cur as { data: unknown }).data
  }
  throw new Error('Invalid Aggregate Members list response')
}

export function messageFromAggregateMutation(res: unknown, fallback: string): string {
  if (!res || typeof res !== 'object') return fallback
  const o = res as { message?: unknown; data?: unknown }
  const dataMsg =
    o.data &&
    typeof o.data === 'object' &&
    typeof (o.data as { message?: unknown }).message === 'string'
      ? (o.data as { message: string }).message.trim()
      : ''
  if (dataMsg) return dataMsg
  if (typeof o.message === 'string' && o.message.trim() && !/^(success|ok)$/i.test(o.message)) {
    return o.message.trim()
  }
  return fallback
}

export async function listAggregateMembers(body: {
  page: number
  groupName?: string
}): Promise<AggregateMembersListResult> {
  const res = await cmApi.getAllAggregateMembers(authHeaders(), body)
  return unwrapAggregateMembersList(res)
}

export async function createAggregateMember(body: {
  groupName: string
  memberList: AggregateMemberListItem[]
  password: string
  confirmPassword: string
}): Promise<string> {
  const res = await cmApi.createAggregateMember(authHeaders(), body)
  return messageFromAggregateMutation(res, 'Aggregator created successfully')
}

export async function updateAggregateMemberList(body: {
  _id: string
  groupName?: string
  memberList?: AggregateMemberListItem[]
  password?: string
  confirmPassword?: string
}): Promise<string> {
  const res = await cmApi.updateAggregateMemberList(authHeaders(), body)
  return messageFromAggregateMutation(res, 'Aggregator updated successfully')
}

export type HierarchyPagePick = {
  _id: string
  username: string
  accessLevel: string
}

export async function searchHierarchyPageForPicker(
  filter: string,
): Promise<HierarchyPagePick[]> {
  const res = await cmApi.searchHierarchyPage(authHeaders(), {
    filter: filter.trim(),
    page: 1,
    noPlayer: true,
  })
  // Picker still uses Lab `{ value }` shape today; Hierarchy Search page maps Standard.
  const lab = res as {
    value?: boolean
    data?: { data?: Array<{ _id?: string; username?: string; accessLevel?: string }> }
  }
  if (!lab.value) return []
  return (lab.data?.data ?? [])
    .filter((r) => r._id && r.username)
    .map((r) => ({
      _id: r._id!,
      username: r.username!,
      accessLevel: r.accessLevel || '',
    }))
}

export function toMemberListItem(pick: HierarchyPagePick): AggregateMemberListItem {
  return {
    _id: pick._id,
    name: pick.username,
    level: pick.accessLevel || 'Player',
  }
}
